import {parseUnifiedDiff} from '@github-ui/agent-sessions/utils/parse-unified-diff'
import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import {GitHubAvatar} from '@github-ui/github-avatar'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {Label, RelativeTime} from '@primer/react'
import {type FC, useMemo} from 'react'
import styles from './FindingCommentPreview.module.css'
import type {CommentPreviewData} from './types'

interface FindingCommentPreviewProps {
  preview: CommentPreviewData
  filePath?: string
  showSuggestionPaths?: boolean
}

export const FindingCommentPreview: FC<FindingCommentPreviewProps> = ({
  preview,
  filePath,
  showSuggestionPaths = true,
}) => {
  const {author, bodyHTML, url, createdAt, diffHunk, suggestion, source} = preview

  // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side
  const safeBodyHTML = bodyHTML as SafeHTMLString
  const suggestionDiffEntries = suggestion?.diffEntries ?? []
  const diffHunkLines = useMemo(() => (diffHunk ? parseUnifiedDiff(diffHunk) : []), [diffHunk])

  return (
    <div className="d-flex flex-column gap-2" data-testid="finding-comment-preview">
      <div className="d-flex flex-items-center flex-wrap gap-2">
        <GitHubAvatar alt={`@${author.login}`} size={20} src={author.avatarUrl} square={author.isBot} />
        <span className="text-semibold text-small">
          {url ? (
            <a href={url} className="Link--primary Link--inTextBlock">
              {author.login}
            </a>
          ) : (
            author.login
          )}
        </span>
        {source === 'copilot' && (
          <Label variant="secondary" aria-label="Generated by AI">
            AI
          </Label>
        )}
        {createdAt && (
          <span className="color-fg-muted text-small">
            {url ? (
              <a href={url} className="Link--secondary Link--inTextBlock">
                <RelativeTime datetime={createdAt} />
              </a>
            ) : (
              <RelativeTime datetime={createdAt} />
            )}
          </span>
        )}
      </div>

      <VerifiedHTMLDiv html={safeBodyHTML} className="markdown-body timeline-comment comment-body tmp-p-3 tmp-my-2" />

      {(diffHunkLines.length > 0 || suggestionDiffEntries.length > 0) && (
        <div className="d-flex flex-column gap-3">
          {diffHunkLines.length > 0 && (
            <div data-testid="finding-comment-diff-hunk">
              <div className="d-flex flex-column overflow-hidden border rounded-2">
                {filePath && (
                  <div className="d-flex flex-items-center tmp-px-3 py-2 text-small gap-2 text-mono color-fg-muted">
                    <span>{filePath}</span>
                  </div>
                )}
                <StaticUnifiedDiffPreview
                  tabSize={4}
                  className={styles.diffPreview}
                  diffTableClassName="borderColor-default"
                  diffLines={diffHunkLines}
                />
              </div>
            </div>
          )}

          {suggestionDiffEntries.map(entry => {
            const key = `${entry.path}-${entry.diffLines
              .map(line => `${line.left ?? ''}:${line.right ?? ''}:${line.type}:${line.text}`)
              .join('|')}`

            return (
              <div key={key} className="d-flex flex-column gap-2" data-testid="finding-comment-suggestion">
                <h4 className="text-small text-semibold color-fg-muted m-0">Suggested change</h4>
                <div className="d-flex flex-column overflow-hidden border rounded-2">
                  {showSuggestionPaths && (
                    <div className="d-flex flex-items-center tmp-px-3 py-2 text-small gap-2 text-mono color-fg-muted">
                      <span>{entry.path}</span>
                    </div>
                  )}
                  <StaticUnifiedDiffPreview
                    tabSize={4}
                    className={styles.diffPreview}
                    diffTableClassName="borderColor-default"
                    diffLines={entry.diffLines}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
